import ArticleLayout from "@/components/feature/ArticleLayout";
import { Link } from "react-router-dom";

const faqs = [
  { q: "Welche Streetwear-Marken finde ich im OOPBUY Spreadsheet?", a: "Das Spreadsheet listet eine Vielzahl von Streetwear-Marken — von etablierten Labels bis zu angesagten Newcomern. Die Hoodies/Sweaters Kategorie wird regelmäßig mit neuen Marken aktualisiert." },
  { q: "Wie finde ich Oversize Hoodies im Spreadsheet?", a: "Im OOPBUY Spreadsheet kannst du nach Größenangaben filtern. Achte auf die Produktbeschreibung, in der Oversize-Passformen speziell gekennzeichnet sind." },
  { q: "Sind die Hoodie-Preise im Spreadsheet aktuell?", a: "Ja, die Preise im OOPBUY Spreadsheet werden wöchentlich aktualisiert und spiegeln die aktuellen Verkaufspreise der gelisteten Verkäufer wider." },
];

export default function StreetwearGuidePage() {
  return (
    <ArticleLayout
      title1="OOPBUY Hoodie &"
      title2="Streetwear Guide"
      title3="Trends und Tipps 2026"
      date="10. Juli 2026"
      readTime="10 Minuten"
      tag="Streetwear"
      seoDescription="Der OOPBUY Streetwear & Hoodie Guide 2026. Entdecke die angesagtesten Marken, finde den perfekten Hoodie und plane komplette Outfits mit dem OOPBUY Spreadsheet — auf Deutsch."
      faqs={faqs}
    >
      <p className="text-lg text-foreground-700 leading-relaxed">
        Streetwear und Hoodies gehören zu den beliebtesten Kategorien im <strong>OOPBUY Spreadsheet</strong>. In diesem Guide zeigen wir dir, wie du die angesagtesten Streetwear-Pieces entdeckst, die besten Deals findest und deinen persönlichen Style mit Hilfe des Spreadsheets perfektionierst.
      </p>

      <div className="my-6">
        <img
          src="https://readdy.ai/api/search-image?query=Trendy%20streetwear%20outfit%20flat%20lay%20with%20oversized%20hoodies%20and%20graphic%20tees%20on%20clean%20concrete%20surface%2C%20natural%20urban%20loft%20lighting%2C%20modern%20street%20fashion%20editorial%20style%2C%20warm%20terracotta%20and%20cream%20color%20palette%2C%20soft%20shadows%2C%20minimalist%20urban%20aesthetic%2C%20professional%20fashion%20photography%2C%20carefully%20curated%20composition&width=1200&height=700&seq=article-streetwear-flatlay-v2&orientation=landscape"
          alt="OOPBUY Streetwear und Hoodie Kollektion"
          className="w-full rounded-xl object-cover"
        />
      </div>

      <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mt-10 mb-4">
        Streetwear Trends 2026 im OOPBUY Spreadsheet
      </h2>
      <p className="text-foreground-700 leading-relaxed">
        Das <strong>OOPBUY Spreadsheet</strong> ist dein Fenster zu den neuesten Streetwear-Trends. Die Kategorien Hoodies/Sweaters und T-Shirts enthalten ständig neue Produkte, die den aktuellen Zeitgeist widerspiegeln. Von minimalistischen Basics bis zu auffälligen Statement-Pieces — das Spreadsheet hat für jeden Style etwas zu bieten.
      </p>

      <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mt-10 mb-4">
        So findest du den perfekten Hoodie
      </h2>
      <div className="space-y-4 my-6">
        {[
          { step: "1", title: "Stilrichtung festlegen", desc: "Überlege, ob du einen klassischen Pullover-Hoodie, einen Zip-Hoodie oder einen Oversize-Look suchst. Das OOPBUY Spreadsheet filtert nach diesen Kategorien." },
          { step: "2", title: "Material prüfen", desc: "Achte im Spreadsheet auf die Materialangaben. Baumwollmischungen sind atmungsaktiv, während French Terry besonders hochwertig ist." },
          { step: "3", title: "Größentabellen nutzen", desc: "Das OOPBUY Spreadsheet enthält Größentabellen für jede Marke. Miss deine Maße und vergleiche sie mit den angegebenen Werten — so vermeidest du Fehlkäufe." },
          { step: "4", title: "Bewertungen lesen", desc: "Andere Käufer teilen oft ihre Erfahrungen zu Passform und Qualität. Diese Informationen findest du ebenfalls im OOPBUY Spreadsheet." },
        ].map((step) => (
          <div key={step.step} className="bg-background-100 rounded-xl p-5 border border-foreground-100/30">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-8 h-8 rounded-full bg-accent-500 text-white flex items-center justify-center text-sm font-bold flex-shrink-0">{step.step}</div>
              <h3 className="font-heading font-semibold text-foreground-900 text-base">{step.title}</h3>
            </div>
            <p className="text-sm text-foreground-600 ml-11">{step.desc}</p>
          </div>
        ))}
      </div>

      <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mt-10 mb-4">
        Streetwear-Marken, die du kennen solltest
      </h2>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 my-6">
        {[
          "Essentials", "Gallery Dept", "Represent", "Rhude", "Aimé Leon Dore",
          "Stüssy", "Palace", "Supreme", "Fear of God", "Kith", "Amiri", "Vlone",
        ].map((brand) => (
          <div key={brand} className="flex items-center gap-2 bg-background-100 rounded-lg px-3 py-2 text-sm text-foreground-700 border border-foreground-100/30">
            <i className="ri-t-shirt-line text-accent-600 w-4 h-4 flex items-center justify-center"></i>
            {brand}
          </div>
        ))}
      </div>

      <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mt-10 mb-4">
        Outfit-Kombinationen mit dem Spreadsheet planen
      </h2>
      <p className="text-foreground-700 leading-relaxed">
        Ein großer Vorteil des <strong>OOPBUY Spreadsheets</strong> ist die Möglichkeit, komplette Outfits zu planen. Da alle Kategorien — von Hoodies über Pants bis zu Accessoires — in einem Spreadsheet vereint sind, kannst du Pieces aus verschiedenen Kategorien kombinieren und dir dein perfektes Streetwear-Outfit zusammenstellen, bevor du bestellst.
      </p>

      <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mt-10 mb-4">
        Häufige Fragen zu Streetwear & Hoodies
      </h2>
      <div className="space-y-4 my-6">
        {faqs.map((faq, i) => (
          <div key={i} className="bg-background-100 rounded-xl p-5 border border-foreground-100/30">
            <h3 className="text-base font-heading font-semibold text-foreground-900 mb-2">{faq.q}</h3>
            <p className="text-sm text-foreground-600 leading-relaxed">{faq.a}</p>
          </div>
        ))}
      </div>

      <div className="mt-10 pt-8 border-t border-foreground-200">
        <h3 className="text-lg font-heading font-semibold text-foreground-900 mb-4">Weiterführende Ressourcen</h3>
        <div className="flex flex-wrap gap-3">
          <Link to="/" className="inline-flex items-center gap-1.5 px-4 py-2 bg-primary-50 text-primary-700 rounded-full text-sm font-medium hover:bg-primary-100 transition-colors cursor-pointer whitespace-nowrap">
            <i className="ri-home-line w-4 h-4 flex items-center justify-center"></i>
            Zur Startseite
          </Link>
          <Link to="/einkaufsguide" className="inline-flex items-center gap-1.5 px-4 py-2 bg-background-100 text-foreground-700 rounded-full text-sm font-medium hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap">
            <i className="ri-shopping-bag-line w-4 h-4 flex items-center justify-center"></i>
            Einkaufsguide
          </Link>
          <Link to="/oopbuy-sneaker-spreadsheet-guide" className="inline-flex items-center gap-1.5 px-4 py-2 bg-background-100 text-foreground-700 rounded-full text-sm font-medium hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap">
            <i className="ri-footprint-line w-4 h-4 flex items-center justify-center"></i>
            Sneaker Guide
          </Link>
        </div>
      </div>
    </ArticleLayout>
  );
}